// src/pages/StoryThreadsHomePage.jsx — the front door of Story Threads Vibe.
//
// Until September 2026 this app opened on MindShield's home page (scam case
// files and a "defence" test), left over from the app it was cloned from.
// This page is built from the app's own module list and toolkit, so everything
// it promises is in the app. Design pass, 30 September 2026: hero with real
// starter questions, the modules as a path, and room to breathe.

import {
  ArrowRight,
  ClipboardList,
  Clock,
  HeartHandshake,
  Lock,
  MessageCircle,
  TrendingUp,
  Trophy,
} from 'lucide-react';
import { useApp } from '../context/AppContext';
import {
  storyThreadsModulesData,
  advancedAddonModules,
  openModule,
} from '../data/storyThreadsModulesMetadata';
import { CONVERSATION_STARTERS } from '../data/toolkitData';
import { STORY_STAGES } from '../data/storyStages';

const STAGES = STORY_STAGES;

const HOW_IT_WORKS = [
  {
    icon: Clock,
    title: 'Short sessions',
    text: 'Each module is split into short sessions you can do one at a time.',
  },
  {
    icon: MessageCircle,
    title: 'Practice at home',
    text: 'Sessions come with practice exercises to try with your own family.',
  },
  {
    icon: Trophy,
    title: 'Progress you can see',
    text: 'You earn XP as you finish modules, and each module unlocks the next.',
  },
];

// Three of the Toolkit's conversation starters, word for word, so the hero
// shows the kind of thing the app actually hands you. Looked up by text, and
// any that no longer exist in the Toolkit are simply dropped.
const HERO_QUESTIONS = [
  "What's something you did that took courage?",
  'What was my parent like as a child/teenager?',
  "What's a family story you think I should know?",
].filter(q => CONVERSATION_STARTERS.some(group => group.questions.includes(q)));

const primaryButton =
  'inline-flex items-center justify-center gap-2 min-h-[48px] px-6 py-3 rounded-xl bg-thread-ember hover:bg-thread-rust text-white font-semibold shadow-thread-md transition-colors';
const secondaryButton =
  'inline-flex items-center justify-center gap-2 min-h-[48px] px-6 py-3 rounded-xl border border-white/25 hover:bg-white/10 text-white font-semibold transition-colors';

const Eyebrow = ({ children }) => (
  <p className="text-sm font-semibold uppercase tracking-[0.18em] text-thread-glow mb-3">
    {children}
  </p>
);

const StoryThreadsHomePage = () => {
  const { setCurrentView, completedModules = [], completedTools = [] } = useApp();
  const [first] = storyThreadsModulesData;
  // The dashboard is not in the menu (Nigel's order), so it needs a way in
  // from here once there is something on it to see.
  const hasProgress = (completedModules?.length || 0) + (completedTools?.length || 0) > 0;

  return (
    <div className="min-h-screen bg-thread-darker text-white">
      {/* Hero */}
      <section className="relative overflow-hidden pt-36 pb-20 md:pt-44 md:pb-28">
        <div className="absolute inset-0 bg-gradient-thread" aria-hidden="true" />
        <div
          className="absolute -top-24 -right-24 w-[28rem] h-[28rem] rounded-full bg-thread-ember/20 blur-3xl"
          aria-hidden="true"
        />
        <div className="relative container mx-auto px-5 max-w-6xl grid gap-14 lg:grid-cols-[1.15fr_1fr] lg:items-center">
          <div>
            <div className="hidden lg:flex items-center gap-3 mb-6">
              <img
                src="/story-threads-vibe-logo-96.webp"
                alt=""
                width="48"
                height="48"
                className="w-12 h-12 rounded-xl shadow-thread-md"
              />
              <p className="text-thread-sand font-semibold tracking-wide">Story Threads Vibe</p>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold font-display leading-[1.08] tracking-tight mb-6">
              Every family is full of stories.{' '}
              <span className="text-thread-glow">Most of them never get asked for.</span>
            </h1>
            <p className="text-lg md:text-xl text-slate-200 leading-relaxed max-w-xl mb-9">
              Learn to find them, tell them well and keep them going: how to get a grandparent
              talking, how to shape a story people remember, and what to do when a family story gets
              hard.
            </p>
            <div className="flex flex-col sm:flex-row flex-wrap gap-3">
              <button
                type="button"
                onClick={() => openModule(setCurrentView, first.id)}
                className={primaryButton}
              >
                Start Module 1 <ArrowRight className="w-5 h-5" aria-hidden="true" />
              </button>
              <button
                type="button"
                onClick={() => setCurrentView('assessment-intro')}
                className={secondaryButton}
              >
                <ClipboardList className="w-5 h-5" aria-hidden="true" /> Take the Vibe check
              </button>
              {hasProgress && (
                <button
                  type="button"
                  onClick={() => setCurrentView('dashboard')}
                  className={secondaryButton}
                >
                  <TrendingUp className="w-5 h-5" aria-hidden="true" /> Your dashboard
                </button>
              )}
            </div>
            <p className="mt-5 text-sm text-slate-400">
              No account needed. Your module progress stays on this device.
            </p>
          </div>

          {HERO_QUESTIONS.length > 0 && (
            <figure className="relative">
              <figcaption className="text-sm font-semibold uppercase tracking-[0.18em] text-thread-sand mb-4">
                Questions to try tonight
              </figcaption>
              <div className="relative">
                {/* the thread that runs through the questions */}
                <span
                  className="absolute left-5 top-6 bottom-6 w-px bg-gradient-to-b from-thread-glow via-thread-ember to-transparent"
                  aria-hidden="true"
                />
                <ul className="relative space-y-4">
                  {HERO_QUESTIONS.map((q, i) => (
                    <li
                      key={q}
                      className={`relative flex items-start gap-4 rounded-2xl border border-white/10 bg-white/[0.06] backdrop-blur-sm p-5 shadow-glass ${
                        i === 1 ? 'lg:ml-8' : ''
                      }`}
                    >
                      <span
                        className="relative z-10 shrink-0 grid place-items-center w-10 h-10 -ml-0.5 rounded-full bg-thread-ember text-white font-bold"
                        aria-hidden="true"
                      >
                        ?
                      </span>
                      <p className="text-lg text-white leading-snug pt-1.5">{q}</p>
                    </li>
                  ))}
                </ul>
              </div>
              <button
                type="button"
                onClick={() => setCurrentView('toolkit')}
                className="mt-4 inline-flex items-center gap-2 min-h-[44px] text-thread-glow hover:text-white font-semibold transition-colors"
              >
                More in the Toolkit <ArrowRight className="w-4 h-4" aria-hidden="true" />
              </button>
            </figure>
          )}
        </div>
      </section>

      {/* The research */}
      <section className="py-20 md:py-28">
        <div className="container mx-auto px-5 max-w-6xl grid gap-12 lg:grid-cols-[1fr_1.35fr]">
          <div>
            <Eyebrow>The research</Eyebrow>
            <h2 className="text-3xl md:text-5xl font-bold font-display leading-tight tracking-tight mb-8">
              Why family stories matter
            </h2>
            <div className="rounded-2xl border border-thread-ember/40 bg-thread-ember/10 p-6 md:p-7">
              <p className="font-display text-4xl md:text-5xl font-bold text-thread-glow mb-3">
                &ldquo;Oscillating&rdquo;
              </p>
              <p className="text-slate-200 leading-relaxed">
                Marshall Duke&apos;s word, talking to Bruce Feiler, for the healthiest kind of
                family story: good times and hard times, and a family that stuck together through
                both.
              </p>
            </div>
          </div>
          <div className="lg:pt-14">
            <div className="space-y-5 text-lg text-slate-300 leading-relaxed max-w-prose">
              <p>
                In a study published in 2008, psychologists Marshall Duke and Robyn Fivush at Emory
                University in the US asked young people twenty questions about their family: where
                their grandparents grew up, how their parents met, what the family had been through.
                The ones who knew more of the answers tended to have higher self-esteem, a stronger
                sense of control over their own lives, and families that got on better.
              </p>
              <p>
                That&apos;s a link, not proof that the stories cause it. The researchers read the
                answers as a sign of something bigger: young people who know these stories tend to
                come from families who talk. And the tidy stories aren&apos;t the ones Duke rates.
                Talking to the journalist Bruce Feiler, he called the healthiest kind
                &ldquo;oscillating&rdquo;: the family had good times and hard times, and stuck
                together through both.
              </p>
            </div>
            <p className="mt-6 pt-5 border-t border-thread-border text-sm text-slate-400 max-w-prose">
              Duke, Lazarus &amp; Fivush (2008),{' '}
              <cite>Psychotherapy: Theory, Research, Practice, Training</cite> 45(2), 268–272.
              Feiler (2013), &ldquo;The Stories That Bind Us&rdquo;, <cite>The New York Times</cite>
              .
            </p>
          </div>
        </div>
      </section>

      {/* The path */}
      <section className="relative py-20 md:py-28 bg-thread-dark/70 border-y border-thread-border">
        <div className="container mx-auto px-5 max-w-6xl">
          <div className="max-w-2xl mb-14">
            <Eyebrow>Your path</Eyebrow>
            <h2 className="text-3xl md:text-5xl font-bold font-display leading-tight tracking-tight mb-4">
              Eight modules, in order
            </h2>
            <p className="text-lg text-slate-300">
              Each one unlocks the next. Finish all eight and four advanced modules open up.
            </p>
          </div>

          <div className="relative">
            {/* the thread down the left of the path */}
            <span
              className="absolute left-[11px] md:left-[15px] top-2 bottom-2 w-0.5 bg-gradient-to-b from-thread-sand via-thread-flame to-thread-gold opacity-60"
              aria-hidden="true"
            />
            <ol className="relative space-y-14">
              {STAGES.map(stage => {
                const modules = storyThreadsModulesData.filter(m => m.level === stage.level);
                if (!modules.length) return null;
                return (
                  <li key={stage.level} className="relative pl-10 md:pl-14">
                    <span
                      className={`absolute left-0 top-1 w-6 h-6 md:w-8 md:h-8 rounded-full ${stage.node} ring-4 ring-thread-dark`}
                      aria-hidden="true"
                    />
                    <h3 className={`text-2xl font-bold font-display ${stage.text} mb-1`}>
                      {stage.level}
                    </h3>
                    <p className="text-slate-300 mb-6 max-w-2xl">{stage.blurb}</p>
                    <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                      {modules.map(m => (
                        <li
                          key={m.id}
                          className="group flex gap-4 rounded-2xl border border-thread-border bg-thread-darker/70 p-5 transition-colors hover:border-thread-ember/60"
                        >
                          <span
                            className={`shrink-0 grid place-items-center w-12 h-12 rounded-xl ring-1 text-2xl ${stage.tint}`}
                            aria-hidden="true"
                          >
                            {m.emoji}
                          </span>
                          <div className="min-w-0">
                            <p className="text-sm text-slate-400 mb-1">
                              Module {m.id} <span aria-hidden="true">·</span> {m.duration}
                            </p>
                            <p className="font-semibold text-white leading-snug mb-1.5">
                              {m.title}
                            </p>
                            <p className="text-sm text-slate-400 leading-relaxed">{m.coolFactor}</p>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </li>
                );
              })}
              <li className="relative pl-10 md:pl-14">
                <span
                  className="absolute left-0 top-1 grid place-items-center w-6 h-6 md:w-8 md:h-8 rounded-full bg-thread-dark ring-2 ring-thread-gold/60"
                  aria-hidden="true"
                >
                  <Lock className="w-3 h-3 md:w-4 md:h-4 text-thread-gold" />
                </span>
                <div className="rounded-2xl border border-dashed border-thread-gold/40 bg-thread-gold/[0.04] p-6">
                  <h3 className="text-2xl font-bold font-display text-thread-gold mb-1">
                    Advanced Addon
                  </h3>
                  <p className="text-slate-300 mb-5">Opens when you finish Module 8.</p>
                  <ul className="grid gap-3 sm:grid-cols-2 text-slate-200">
                    {advancedAddonModules.map(m => (
                      <li key={m.id} className="flex items-baseline gap-3">
                        <span className="text-sm text-slate-400 shrink-0">Module {m.id}</span>
                        <span className="font-medium">{m.title}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </li>
            </ol>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-20 md:py-28">
        <div className="container mx-auto px-5 max-w-6xl">
          <Eyebrow>How it works</Eyebrow>
          <h2 className="text-3xl md:text-5xl font-bold font-display leading-tight tracking-tight mb-12">
            A little at a time
          </h2>
          <ol className="grid gap-6 md:grid-cols-3">
            {HOW_IT_WORKS.map(({ icon: Icon, title, text }, i) => (
              <li
                key={title}
                className="relative rounded-2xl border border-thread-border bg-white/[0.04] p-7"
              >
                <div className="flex items-center justify-between mb-6">
                  <span className="grid place-items-center w-12 h-12 rounded-xl bg-thread-ember/15 ring-1 ring-thread-ember/40">
                    <Icon className="w-6 h-6 text-thread-glow" aria-hidden="true" />
                  </span>
                  <span
                    className="font-display text-5xl font-bold text-white/10"
                    aria-hidden="true"
                  >
                    {i + 1}
                  </span>
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">{title}</h3>
                <p className="text-slate-300 leading-relaxed">{text}</p>
              </li>
            ))}
          </ol>

          <div className="mt-16 flex flex-col md:flex-row gap-6 md:items-center rounded-2xl border border-thread-border border-l-4 border-l-thread-sand bg-thread-dark/70 p-6 md:p-8">
            <span className="shrink-0 grid place-items-center w-14 h-14 rounded-2xl bg-thread-sand/10 ring-1 ring-thread-sand/30">
              <HeartHandshake className="w-7 h-7 text-thread-sand" aria-hidden="true" />
            </span>
            <div className="flex-1">
              <h2 className="text-2xl font-bold font-display mb-2">When a story is hard to hear</h2>
              <p className="text-slate-300 leading-relaxed">
                Not every family story is a happy one. Module 7 covers the hard ones: conflict,
                separation, loss and mental health. If a story brings up something heavy for you,
                you don&apos;t have to carry it on your own.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setCurrentView('resources')}
              className="shrink-0 inline-flex items-center gap-2 min-h-[44px] text-thread-glow hover:text-white font-semibold transition-colors"
            >
              Where to find support <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </button>
          </div>
        </div>
      </section>

      {/* Closing call to action */}
      <section className="pb-24 md:pb-32">
        <div className="container mx-auto px-5 max-w-6xl">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-thread-rust via-thread-ember to-thread-rust px-6 py-14 md:px-14 md:py-16 text-center shadow-thread-xl">
            <div
              className="absolute -bottom-24 -left-16 w-80 h-80 rounded-full bg-thread-gold/20 blur-3xl"
              aria-hidden="true"
            />
            <div className="relative">
              <h2 className="text-3xl md:text-5xl font-bold font-display leading-tight tracking-tight mb-4">
                Start with the first thread
              </h2>
              <p className="text-lg text-orange-50 mb-8">
                Module 1, {first.title}, takes about {first.duration}.
              </p>
              <button
                type="button"
                onClick={() => openModule(setCurrentView, first.id)}
                className="inline-flex items-center justify-center gap-2 min-h-[48px] px-7 py-3 rounded-xl bg-white text-thread-rust hover:bg-orange-50 font-semibold shadow-thread-lg transition-colors"
              >
                Open Module 1 <ArrowRight className="w-5 h-5" aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default StoryThreadsHomePage;
